import { useRef, useState } from 'react';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  Pager,
  Spinner,
  Table,
  Td,
  Th,
  cx,
} from '@/components/ui';
import {
  changeColorClass,
  formatDate,
  formatDateTime,
  formatKrw,
  formatPercent,
  formatSignedNumber,
  toThousands,
} from '@/lib/format';
import { Modal } from '@/components/Modal';
import { StockDetailModal } from './StockDetailModal';
import { QuoteCell, SortableTh, StackedSortTh } from './components';
import { resolveQuote } from './priceDisplay';
import {
  DEFAULT_POSITION_SORT,
  nextPositionSort,
  positionSortLabel,
  sortPositions,
  type PositionSort,
} from './bandPositionSort';
import type { Price } from './stockTypes';
import { AccountStatsCard } from './AccountStatsCard';
import { daysHeldLabel } from './accountStats';
import { HelpButton } from './HelpButton';
import { PROFIT_HELP } from './profitHelp';
import {
  sortByBuyOrder,
  sortTargets,
  targetBuyState,
  type TargetBuyState,
  type TargetSort,
} from './bandDisplay';
import { usePrices } from './useStocks';
import { withLivePrice } from './bandLivePrice';
import type { IndicatorHelp } from './indicatorHelp';
import type {
  BandClosed,
  BandConfig,
  BandPosition,
  BandReview,
  BandStatus,
  BandTarget,
  BandVariant,
} from './bandTypes';
import { useBandStatus } from './usePaper';
import { BandPriceFormatContext, bandPriceFormat, useBandPriceFormat } from './bandCurrency';
import { REVIEW_PAUSED_NOTE } from './reviewPause';

/**
 * 저점매수(4번째 계좌) 탭. (docs/impl/85)
 *
 * 🔴 화면 표기는 '저점매수', 코드·DB 는 BAND 다 (2026-08-28 사용자 요청으로 개명).
 *
 * 사용자의 실제 매매 감각을 그대로 굴리는 계좌다:
 * "하이닉스가 130~140에 오면 산다. 더 떨어지면 더 산다. 오르면 판다."
 *
 * 화면이 답해야 하는 질문 순서대로 배치한다. (2026-09-12 사용자 요청으로 재배치)
 *   지금 얼마인가(계좌) → 뭘 들고 있나(보유) → 어땠나(청산·회고) → 뭘 노리나(감시 대상)
 *
 * 🔴 섹션마다 길이를 다루는 방법이 다르다. 성격이 달라서다
 *
 *   보유     지금 행동할 대상이다. 종목당 예산(20% · 챌린저 10%에 8종목)이 개수의 상한을 만든다 → 전부 펼침
 *   청산     끝난 기록이고 회차마다 쌓이기만 한다 → 페이지 (10건씩)
 *   회고     읽을거리다. 한 건이 길다 → 페이지 (5건씩)
 *   감시     참조 목록이고 설정값으로 고정이다 → 맨 아래에서 전부 펼침
 *
 *   🔴 감시 대상이 맨 아래인 이유
 *     100종목이라 어디에 두든 길다. 맨 아래면 뒤에 아무것도 없어 다른 것을 가리지
 *     않으므로 스크롤 상자가 필요 없어진다. 2026-09-11 에 세로 스크롤 상자로
 *     해결하려다 "페이지 스크롤 안의 스크롤" 이 더 불편하다는 피드백을 받았다.
 *
 *   무한 스크롤은 쓰지 않는다 - 아래에 다른 섹션이 있으면 영영 닿지 못한다.
 *   토글 탭도 쓰지 않는다 - 보유와 청산을 나란히 놓고 "이건 왜 아직 안 팔렸나" 를
 *   보는 화면이라 하나를 숨기면 그 비교가 끊긴다.
 *
 * 🔴 기존 3계좌와 다른 규칙이 세 개다. 화면에 그 차이를 밝힌다
 *   고정 유니버스(발굴 아님) · 분할매수 허용(저쪽은 금지) · 만료 없음(선까지 간다)
 */
/**
 * 청산 목록 한 페이지 분량. 추천 3계좌(PaperPage.CLOSED_PAGE_SIZE)와 같은 값이다.
 *
 * 🔴 청산에만 페이지를 둔다 (2026-09-12 사용자 요청)
 *   다섯 섹션의 성격이 다르다. 보유는 지금 행동할 대상이라 전부 펼치고,
 *   감시 대상은 100 고정이라 맨 아래에서 전부 펼친다. 청산만 회차를 거듭할수록
 *   **끝없이 늘어나고** 그중 궁금한 것은 최근 몇 건이다 - 페이지가 맞는 자리다.
 */
const CLOSED_PAGE_SIZE = 10;

/**
 * @param variant 계좌 변형. 보수·공격 두 탭이 같은 컴포넌트를 쓴다 (V119)
 */
export function BandPanel({ variant }: { variant: BandVariant }) {
  const { data, isLoading, error, refetch } = useBandStatus(variant);
  const [openStockId, setOpenStockId] = useState<number | null>(null);
  const [sort, setSort] = useState<TargetSort>({ key: null, dir: 'asc' });
  /*
    감시 대상을 무엇으로 줄 세우나 (2026-09-19 사용자 요청 - "매수 우선순위로 미리 보고 싶다", I-62)
      rank  왕복성 순위(기본). 열 머리를 누르면 그 열로 정렬한다
      buy   매수 순서(예상) - sortByBuyOrder 주석. 열 머리를 누르면 rank 로 돌아간다
  */
  const [targetOrder, setTargetOrder] = useState<'rank' | 'buy'>('rank');
  const sortByColumn = (next: TargetSort) => {
    setTargetOrder('rank');
    setSort(next);
  };
  const [columnHelpOpen, setColumnHelpOpen] = useState(false);
  // 계좌 카드의 [N종목이 매수 구간] 을 누르면 이 표로 뛴다
  const targetsRef = useRef<HTMLDivElement>(null);
  // 보유 정렬 (2026-09-16). 기본은 마지막 매수 최신순 - bandPositionSort.ts
  const [positionSort, setPositionSort] = useState<PositionSort>(DEFAULT_POSITION_SORT);

  /*
    현재가는 15초 폴링으로 덧입힌다 (2026-08-28 사용자 요청 - 추천 탭과 같은 주기).
    상태 응답에도 시세가 들어 있지만 그건 2분 주기라, 감시 대상·보유의 현재가만
    관심종목과 같은 15초 시세 훅으로 갱신한다. 서버 값은 폴백이다.
  */
  const priceIds = [
    ...(data?.targets.map((t) => t.stockId) ?? []),
    ...(data?.positions.map((p) => p.stockId) ?? []),
  ];
  const livePrices = usePrices([...new Set(priceIds)]);
  const priceOf = (stockId: number) =>
    livePrices.data?.find((p) => p.stockId === stockId) ?? null;

  if (isLoading) {
    return <Spinner label="저점매수 현황을 불러오는 중" />;
  }
  if (error) {
    return <ErrorState message={error.message} onRetry={() => void refetch()} />;
  }
  if (!data || !data.ready || !data.account) {
    return (
      <Card>
        <EmptyState
          title="저점매수 계좌가 아직 없습니다"
          description="배포 직후라면 계좌를 만드는 마이그레이션이 도는 중입니다. 잠시 후 새로고침하세요."
        />
      </Card>
    );
  }

  const { account } = data;
  // 🇺🇸 가격 칸의 통화 (2026-09-28, 122번 12.14) — 합계는 늘 원화, 종목 가격은 미국이면 달러(bandCurrency.ts)
  const priceFormat = bandPriceFormat(account.currency);
  const usd = priceFormat.usd;
  // 미국은 매수 분위가 0 이면 아직 굴리지 않는다(122번 12.10) — ✏️ 10/11 서버가 4회차 전엔 0 으로 싣는다(BandRoundGate · 4회차를 열면 저절로 켜짐)
  const usIdle = usd && (data.config?.buyPercentile ?? 0) === 0;

  /*
    🔴 보유 칸의 현재가·평가액·수익률은 15초 시세로 다시 계산한다 (2026-09-16 사용자 요청, BACKLOG I-54)
      상태 응답은 2분 주기라, 같은 칸 안에서 "전날 대비"(15초)와 수익률·현재가(2분)가 어긋났다.
      정렬([수익률])도 이 목록을 쓰므로 화면 숫자와 정렬 순서가 같은 값을 본다.
      계좌 카드(총자산)는 서버가 예수금과 전 종목 평가를 합산한 값이라 2분 그대로 둔다.
  */
  const livePositions = data.positions.map((position) =>
    withLivePrice(position, priceOf(position.stockId)?.lastPrice ?? null),
  );

  /*
    매수 구간에 들어온 종목 수. (2026-09-12 사용자 승인)

    🔴 감시 대상이 맨 아래로 내려가면서 생긴 단점을 덮는다
      "오늘 뭐 살 것 같나" 를 보려고 매번 끝까지 스크롤해야 한다. 그 답만
      계좌 카드 아래에 한 줄로 올리고, 누르면 그 표로 뛴다.

    표의 배지와 같은 함수(targetBuyState)로 세므로
    "요약은 8종목인데 배지는 7개" 같은 어긋남이 생기지 않는다.

    🔴 "실제로 살 수 있는" 것만 센다 (2026-09-19 I-62)
      보유 중 · 손절선 아래 · 순위 밖(챌린저 30)은 기준가 아래여도 사지 않는다.
  */
  const maxSwingRank = data.config?.maxSwingRank ?? null;
  const heldIds = new Set(data.positions.map((p) => p.stockId));
  const livePriceOf = (stockId: number) => priceOf(stockId)?.lastPrice ?? null;
  const buyStateOf = (target: BandTarget): TargetBuyState =>
    targetBuyState(target, livePriceOf(target.stockId), maxSwingRank, heldIds.has(target.stockId));
  const inBandCount = data.targets.filter((target) => buyStateOf(target) === 'buy').length;
  // 챌린저만 - 순위 끝이 감시 수보다 작을 때 "그중 상위 30" 을 말한다
  const rankLimited = maxSwingRank !== null && maxSwingRank < data.targets.length;

  /*
    설정값과 실제 목록 수가 다를 수 있다. (2026-09-12)

    감시 대상은 하루 한 번(평일 21:00) 다시 뽑는다. 설정을 바꾸면 그 갱신 전까지는
    화면의 목록이 옛 수 그대로다 - 실제로 2026-09-12 에 50 → 100 으로 바꾼 뒤
    월요일 갱신까지 하루 반 동안 도움말은 100, 목록은 50 이었다.

    🔴 어느 한쪽을 지우지 않고 둘 다 말한다
      설정만 말하면 화면이 거짓말이 되고, 실제만 말하면 "왜 안 바뀌지" 를 알 수 없다.
      둘이 같아지면 이 안내는 저절로 사라진다 - 재배포가 필요 없다.
  */
  const configuredSize = data.config?.universeSize ?? null;
  const pendingResize =
    configuredSize !== null && data.targets.length > 0 && configuredSize !== data.targets.length;

  return (
    <BandPriceFormatContext.Provider value={priceFormat}>
    <div className="space-y-4">
      {/*
        🇺🇸 아직 굴리지 않는 미국 계좌 (2026-09-28, 122번 12.10)
          계좌 행 · 감시 · 잡은 다 있지만 매수 분위가 0 이라 사지 않는다. 비어 있는 화면을 "살 게 없다" 로
          읽지 않게, 무엇이 남았는지를 먼저 말한다.
      */}
      {usIdle && (
        <Card>
          <p className="text-sm text-fg">
            🇺🇸 미국 저점매수는 <strong>4회차부터</strong> 굴립니다. 지금은 계좌와 화면만 준비되어 있고,
            감시 대상 뽑기와 매수는 꺼져 있습니다.
          </p>
        </Card>
      )}
      {/*
        ── 계좌 ──
        숫자 세 줄은 추천 3계좌와 같은 AccountStatsCard 가 그린다 (2026-09-12).
        저점매수는 총자산 이름이 equity 라 total 로 옮겨 담는다 - 나머지 필드는 이름이 같다.
      */}
      <AccountStatsCard title="계좌" stats={{ ...account, total: account.equity }}>
        <p className="mt-3 text-xs text-fg-subtle">
          이번 회차 {formatDate(account.startedAt)} 시작 · 보유 {data.positions.length}종목
        </p>
        <p className="mt-1 text-xs text-fg-subtle">
          {/* 08~20시는 BandIntradayJob(장전·애프터마켓 포함). 단 수는 변형별이다 - 챌린저는 한 번에 (2026-09-19 I-59) */}
          {/* 🇺🇸 미국은 BandUsIntradayJob — 정규장만(서머타임 22:30~05:00 · 표준시 23:30~06:00 KST) */}
          {usd
            ? '미국 정규장(한국 시각 밤 10:30~새벽 5:00, 11월부터는 밤 11:30~새벽 6:00) 동안 1분마다 감시합니다. 매수 기준가 아래로 오면 사고'
            : '평일 08:00~20:00 1분마다 감시합니다. 매수 기준가 아래로 오면 사고'}
          {(data.config?.maxRungs ?? 3) > 1
            ? `(최대 ${data.config?.maxRungs ?? 3}번 나눠서)`
            : '(종목당 한도를 한 번에)'}
          , 평단 +{data.config?.bouncePct ?? 3}% 반등에 팝니다. 실제 주문은 나가지 않습니다.
        </p>
        {/*
          🇺🇸 원화 계좌로 달러 종목을 산다 (사용자 9/20 Q1) — 위 합계는 원화, 아래 가격은 달러.
            환율 몫을 갈라 보인다: 종목은 벌었는데 환율로 잃었는지(또는 반대)를 한 줄로 읽게.
        */}
        {usd && (
          <p className="mt-1 text-xs text-fg-subtle">
            원화 계좌로 달러 종목을 삽니다. 살 때 · 팔 때 그 순간 환율로 바꿉니다.{' '}
            {account.fxRate !== null
              ? `지금 환율 ${formatKrw(account.fxRate)} 기준 · `
              : '지금 환율을 못 받아 보유는 원화로 낸 돈으로 평가했습니다 · '}
            환율 몫{' '}
            <span className={cx('tabular', changeColorClass(account.fxPnl ?? 0))}>
              {/* 환율 몫은 원화 금액이다 — 가격 통화(달러)가 아니라 원화 포맷으로 */}
              {bandPriceFormat('KRW').signedPx(account.fxPnl)}
            </span>
          </p>
        )}

        {/*
          매수 구간 요약. 감시 대상 표가 맨 아래라 여기서 한 줄로 답한다.
          0종목이면 그것도 답이다 - "지금은 살 자리가 없다" 를 말한다.
        */}
        {data.targets.length > 0 && (
          <button
            type="button"
            onClick={() =>
              targetsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
            className="mt-2 text-xs text-brand hover:underline"
          >
            감시 {data.targets.length}종목
            {rankLimited && `(새로 사는 건 상위 ${maxSwingRank})`} 중{' '}
            <strong className={inBandCount > 0 ? 'text-rise' : undefined}>
              {inBandCount}종목이 매수 구간
            </strong>{' '}
            ↓
          </button>
        )}
      </AccountStatsCard>

      {/* ── 보유 (사다리) ── */}
      {data.positions.length > 0 && (
        <Card
          title={
            /*
              칸의 숫자들이 무엇인지 - "보유 N종목" 바로 오른쪽 (2026-09-16 사용자 요청)
              🔴 도움말은 프로젝트 전체가 물음표(?)로 통일한다. 한때 돋보기를 썼다가 같은 날 되돌렸다
            */
            <span className="inline-flex items-center">
              보유 {data.positions.length}종목
              <HelpButton help={holdingsHelp(data.config)} />
            </span>
          }
          action={
            /*
              정렬 필터 둘 (2026-09-16 사용자 요청).
              같은 버튼을 다시 누르면 방향이 바뀐다. 켜진 버튼만 지금 방향을 글자로 말한다.
            */
            <div className="flex flex-wrap justify-end gap-1">
              {(['buy', 'return'] as const).map((key) => (
                <button
                  key={key}
                  type="button"
                  aria-pressed={positionSort.key === key}
                  onClick={() => setPositionSort(nextPositionSort(positionSort, key))}
                  className={cx(
                    'rounded-full px-2.5 py-1 text-xs transition-colors',
                    positionSort.key === key
                      ? 'bg-brand text-brand-fg'
                      : 'bg-surface-hover text-fg-muted hover:text-fg',
                  )}
                >
                  {positionSortLabel(key, positionSort)}
                </button>
              ))}
            </div>
          }
        >
          <ul className="flex flex-col gap-2">
            {sortPositions(livePositions, positionSort).map((position) => (
              <PositionRow
                key={position.id}
                position={position}
                price={priceOf(position.stockId)}
                onOpen={() => setOpenStockId(position.stockId)}
              />
            ))}
          </ul>
        </Card>
      )}

      <ColumnHelpModal
        open={columnHelpOpen}
        onClose={() => setColumnHelpOpen(false)}
        buyPercentile={data.config?.buyPercentile ?? 20}
        bandWindow={data.config?.bandWindow ?? 120}
      />

      {/* ── 청산 이력 (보유 다음. 2026-09-12 사용자 요청) ── */}
      <ClosedCard data={data} onOpen={setOpenStockId} />

      {data.reviews.length > 0 && <BandReviews reviews={data.reviews} />}

      {/* ── 감시 대상 (맨 아래. 2026-09-12 사용자 요청) ── */}
      <div ref={targetsRef}>
      <Card
        title={`감시 대상 ${data.targets.length}종목`}
        action={
          <button
            type="button"
            onClick={() => setColumnHelpOpen(true)}
            aria-label="감시 대상 표의 각 열 설명"
            className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-weak text-xs font-semibold text-brand transition-colors hover:bg-brand hover:text-brand-fg"
          >
            ?
          </button>
        }
      >
        {data.targets.length === 0 ? (
          /*
            🇺🇸 미국은 장중에 감시 대상을 계산하지 않는다 (2026-09-28 I-78 2차)
              장중 감시(BandUsIntradayJob)는 읽기만 하고, 계산은 매일 10:40 BandUsTargetJob 한 곳이다.
              매수 분위가 0 인 동안은 그 잡도 아무것도 만들지 않는다 - 국내 문구를 그대로 쓰면 거짓말이 된다.
          */
          <EmptyState
            title="감시 대상이 아직 없습니다"
            description={
              usIdle
                ? '미국은 매일 10:40(한국 시각)에 감시 대상을 뽑습니다. 4회차 전에는 매수가 꺼져 있어 뽑지 않고 비어 있습니다.'
                : usd
                  ? '미국은 매일 10:40(한국 시각)에 그날 확정 봉으로 감시 대상을 뽑습니다.'
                  : '장중 첫 감시 주기에 왕복성 상위 종목이 계산됩니다.'
            }
          />
        ) : (
          <>
            {/*
              🔴 세로 스크롤을 걷어냈다 (2026-09-12 사용자 요청)

                하루 전에는 100종목 표를 max-h + overflow-auto 상자에 넣고 머리를
                sticky 로 고정했다. 실제로 써 보니 불편했다 - 페이지 스크롤 안에
                또 스크롤이 있고, 열이 넷이라 가로로도 밀려서 손가락이 어디로 가야
                할지 알 수 없었다. **중첩 스크롤이 문제였다.**

                고친 방법 둘
                  ① 이 카드를 화면 맨 아래로 옮겼다. 뒤에 아무것도 없으므로
                     목록이 길어도 다른 것을 가리지 않는다. 그래서 전부 펼친다
                  ② 열을 넷에서 셋으로 줄였다. 매수 기준가와 기준가까지를 한 칸에
                     두 줄로 쌓으면 폰에서 가로 스크롤이 아예 안 생긴다
                     (주도주·섹터의 대비/등락률과 같은 방식)

                공유 Table 을 그대로 쓴다. 직접 그린 래퍼가 필요했던 이유가
                sticky 머리였는데 그것이 없어졌다.
            */}
            {/* 줄 세우기 전환 (2026-09-19 I-62). 모바일에서도 한 줄에 들어가게 짧게 둔다 */}
            <div className="mb-2 flex flex-wrap gap-1" role="tablist" aria-label="감시 대상 줄 세우기">
              {(
                [
                  ['rank', '왕복성 순위'],
                  ['buy', '매수 순서(예상)'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={targetOrder === key}
                  onClick={() => setTargetOrder(key)}
                  className={cx(
                    'rounded-full px-3 py-1 text-xs',
                    targetOrder === key ? 'bg-brand text-brand-fg' : 'bg-surface-hover text-fg-muted',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            {targetOrder === 'buy' && (
              <p className="mb-2 text-xs text-fg-subtle">
                1분마다 감시해서 <strong>먼저 매수 기준가에 닿은 종목부터</strong> 삽니다. 같은 분에
                여러 종목이 닿으면 왕복성 순위가 높은 것부터 삽니다. 그래서 지금 매수 구간인 종목을
                순위 순으로, 그다음은 기준가에 가까운 순으로 세웠습니다.{' '}
                {/* 보유 상한이 있는 계좌(챌린저)는 자리가, 없는 계좌는 예수금이 끊는다 */}
                {data.config?.maxPositions != null
                  ? `자리가 ${data.config.maxPositions}개뿐이라 앞에서부터 차면 뒤는 못 삽니다.`
                  : '예수금이 모자라면 뒤는 못 삽니다.'}{' '}
                "안 삼" 표시는 기준가 아래여도 사지 않는 종목입니다.
              </p>
            )}
            <Table>
              <thead>
                <tr>
                  <Th>{targetOrder === 'buy' ? '종목 (앞: 순위)' : '종목'}</Th>
                  <SortableTh
                    label="현재가"
                    sortKey="current"
                    sort={targetOrder === 'buy' ? { key: null, dir: 'asc' } : sort}
                    onSort={sortByColumn}
                  />
                  <StackedSortTh
                    topLabel="매수 기준가"
                    topKey="band"
                    bottomLabel="기준가까지"
                    bottomKey="distance"
                    sort={targetOrder === 'buy' ? { key: null, dir: 'asc' } : sort}
                    onSort={sortByColumn}
                  />
                </tr>
              </thead>
              <tbody>
                {(targetOrder === 'buy'
                  ? sortByBuyOrder(data.targets, livePriceOf, maxSwingRank, heldIds)
                  : sortTargets(data.targets, sort)
                ).map((target) => (
                  <TargetRow
                    key={target.stockId}
                    target={target}
                    livePrice={livePriceOf(target.stockId)}
                    buyState={buyStateOf(target)}
                    onOpen={() => setOpenStockId(target.stockId)}
                  />
                ))}
              </tbody>
            </Table>
            {/*
              갱신 시점을 밝힌다 (2026-08-28 사용자 요청 - 추천 탭과 같은 원칙).
              현재가는 15초 폴링, 매수 기준가는 하루 한 번(전일 일봉까지) 계산이다.
              두 주기가 달라서 각각 적는다.
            */}
            <p className="mt-3 text-xs text-fg-subtle">
              현재가는 15초마다 갱신되며, 장이 닫혀 있으면 마지막 종가입니다.
              {usd && ' 미국 종가 날짜는 미국 거래일 기준입니다.'}
              {livePrices.dataUpdatedAt > 0 &&
                ` (마지막 갱신 ${new Date(livePrices.dataUpdatedAt).toLocaleTimeString(
                  'ko-KR',
                  { hour: '2-digit', minute: '2-digit', second: '2-digit' },
                )})`}
              {data.bandBaseDate &&
                ` 매수 기준가는 ${data.bandBaseDate} 종가까지로 계산한 값입니다.`}
            </p>

            {/*
              설정과 실제가 다른 동안만 뜬다. 같아지면 저절로 사라진다 -
              이 안내를 지우려고 다시 배포할 일이 없다.
            */}
            {pendingResize && (
              <p className="mt-1 text-xs text-warn">
                설정은 {configuredSize}종목인데 지금 목록은 {data.targets.length}종목입니다. 감시
                대상은 {usd ? '매일 오전 10:40(미국 확정 종가 뒤)' : '평일 21:00'} 에 다시 뽑으므로 그때 반영됩니다.
              </p>
            )}
          </>
        )}
      </Card>
      </div>

      {data.positions.length === 0 && data.closed.length === 0 && (
        <Card>
          <EmptyState
            title="아직 매매가 없습니다"
            description="감시 대상이 매수 기준가 아래로 내려오면 첫 매수가 일어납니다. 저점매수는 기다림이 규칙입니다 - 며칠 조용해도 정상입니다."
          />
        </Card>
      )}

      <StockDetailModal stockId={openStockId} onClose={() => setOpenStockId(null)} />
    </div>
    </BandPriceFormatContext.Provider>
  );
}

/**
 * 보유 한 종목. 사다리가 몇 단인지, 다음 매수·매도·손절선이 어디인지 보인다.
 *
 * 네 선(평단·목표·손절·다음 단)을 다 보여주는 이유 - 이 전략의 다음 행동은
 * 전부 이 선들에서 나온다. 숨기면 "왜 샀지/팔았지" 를 복기할 수 없다.
 */
function PositionRow({
  position,
  price,
  onOpen,
}: {
  position: BandPosition;
  /** 15초 시세. 전날 종가 대비를 여기서 얻는다. 아직 못 받았으면 null */
  price: Price | null;
  onOpen: () => void;
}) {
  // 전날 종가 대비 (2026-09-16). 관심종목 표와 같은 계산이다
  const quote = resolveQuote(price ?? undefined, null, null);
  // 🇺🇸 가격 칸 통화 — 미국이면 달러 (bandCurrency.ts)
  const { px, signedPx } = useBandPriceFormat();
  /*
    🔴 2단 이상이면 처음부터 펼친다. (2026-09-10 사용자 요청)

      1단은 접어 두는 편이 낫다 - 체결이 하나뿐이라 펼쳐도 위에 적힌 평단과
      같은 값이 한 줄 더 나올 뿐이다. 실제로 보유의 85%가 1단이라
      전부 펼치면 목록이 두 배로 길어지기만 한다.

      2단부터는 다르다. 평단이 어떻게 내려왔는지가 그 자리에서만 보이고,
      그것이 이 계좌에서 드문 일이다(76건 중 11건). 드물고 중요한 것을
      한 번 더 눌러야 보이게 두면 지나치게 된다.

    🔁 2026-09-16 밤 — 사다리(펼치기 목록)를 없앴다 (사용자 결정)
      같은 날 "매수" 줄에 차수별 시각을 올리자 사다리에 남은 것이 차수별 가격·수량뿐이었다.
      그것까지 매수 줄에 합쳐 같은 정보가 두 곳에 나오지 않게 했고, 펼치기 버튼도 필요 없어졌다.
      위 설명은 사다리를 두던 때의 판단 기록으로 남긴다.
  */

  // 🔴 매수 체결은 최근 것이 위 (2026-09-16 사용자 요청). 같은 분이면 차수가 큰 것이 나중이다
  const buysNewestFirst = position.fills
    .filter((fill) => fill.type === 'BUY')
    .sort((a, b) => b.filledAt.localeCompare(a.filledAt) || b.rungNo - a.rungNo);

  return (
    <li className="rounded-lg border border-line px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onOpen}
          className="font-medium text-fg hover:underline"
        >
          {position.stockName}
        </button>
        <Badge tone="info">{position.rungs}단</Badge>
        {/*
          평단 × 수량 옆에 그 곱을 붙인다. (2026-09-10 사용자 요청)
          단가와 주수만 있으면 이 종목에 얼마가 들어갔는지 매번 암산해야 한다.
        */}
        <span className="tabular text-sm text-fg">
          {px(position.avgPrice)} × {position.quantity}주
          <span className="ml-1 text-xs text-fg-subtle">
            = {px(position.avgPrice * position.quantity)}
          </span>
        </span>
        {position.unrealizedPercent !== null && (
          <span className={cx('tabular text-sm', changeColorClass(position.unrealizedPercent))}>
            {formatPercent(position.unrealizedPercent)}
          </span>
        )}
      </div>

      {/*
        🔴 전날 종가 대비 - 평단 대비와 따로 한 줄로 (2026-09-16 사용자 요청)

          위의 % 는 "산 가격보다 얼마나 내려와 있나" 이고, 이 줄은 "오늘 하루 오르고 있나" 다.
          사용자가 보려는 것 - 평단보다 한참 빠진 종목이 전날보다 오르기 시작하는 순간.
          둘을 한 칸에 섞으면 어느 기준의 +/− 인지 헷갈리므로 줄을 나눈다.

          🔴 자리는 평단 × 주수 = 합계 바로 아래다 (2026-09-16 사용자 요청)
            사용자가 수익률과 전날 대비를 가장 중요하게 본다. 매수 시각·목표·손절 줄 밑에
            있으면 눈이 한 번 더 내려가야 해서 두 숫자를 위로 모았다.

          보유분 = 한 주당 대비 × 보유 수량. "이 종목이 오늘 계좌를 얼마 움직였나" 다.
          시세를 아직 못 받았으면 줄을 그리지 않는다 - 없는 값을 0 으로 보이지 않는다.
      */}
      {quote.change !== null && (
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-fg-muted">
          <span className="inline-flex items-center gap-1">
            전날 대비 <QuoteCell view={quote} showPrice={false} />
          </span>
          <span>
            보유분{' '}
            <span className={cx('tabular', quote.stale ? 'text-fg-subtle' : changeColorClass(quote.change))}>
              {signedPx(quote.change * position.quantity)}
            </span>
          </span>
        </div>
      )}

      {/*
        🔴 아래 세 묶음은 줄을 나눈다 (2026-09-16 밤 사용자 요청 - "연달아 나오면 가독성이 떨어진다")
          ① 매수 - 차수마다 한 줄, 최근 매수가 위 (2차 줄 다음에 1차 줄)
          ② 현재 · 평가액
          ③ 목표 · 다음 매수 · 손절 - 가격이 높은 순서라 위에서 아래로 떨어지는 선과 같은 순서다

        첫 매수 시각을 접지 않고 보여주는 이유 (2026-08-31) - "언제 샀나" 는 접어 둘 정보가 아니다.
        "며칠째" 는 1차부터 센다 (2026-09-12) - 이 종목에 돈이 묶인 기간이다. 그래서 1차 줄 끝에 붙인다.
        사다리(펼치기 목록)는 2026-09-16 에 없애고 차수별 가격 × 수량을 매수 줄로 합쳤다.
        1단이면 가격·수량은 첫 줄의 평단 × 주수와 같아 시각만 둔다.
      */}
      <div className="mt-1 space-y-0.5 text-xs text-fg-muted">
        {position.rungs >= 2 && buysNewestFirst.length >= 2 ? (
          buysNewestFirst.map((fill) => (
            <div key={`${fill.rungNo}-${fill.filledAt}`}>
              매수 <RungBadge rungNo={fill.rungNo} />{' '}
              <span className="tabular text-fg">{formatDateTime(fill.filledAt)}</span>{' '}
              <span className="tabular text-fg">
                {px(fill.price)} × {fill.quantity}주
              </span>
              {fill.rungNo === 1 && (
                <>
                  {' · '}
                  {daysHeldLabel(position.daysHeld)}
                </>
              )}
            </div>
          ))
        ) : (
          <div>
            매수 <span className="tabular text-fg">{formatDateTime(position.firstBuyAt)}</span>
            {' · '}
            {daysHeldLabel(position.daysHeld)}
          </div>
        )}
      </div>

      {position.currentPrice !== null && (
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-fg-muted">
          {/*
            🔴 현재가에 색을 넣는다 (2026-09-10 사용자 요청)
              평단보다 오르면 빨강, 내리면 파랑. 옆의 수익률과 같은 방향을 가리켜
              눈이 한 번에 읽는다.
          */}
          <span>
            현재{' '}
            <span
              className={cx(
                'tabular',
                changeColorClass(position.currentPrice - position.avgPrice),
              )}
            >
              {px(position.currentPrice)}
            </span>
          </span>
          <span>
            평가액{' '}
            <span className="tabular text-fg">
              {px(position.currentPrice * position.quantity)}
            </span>
          </span>
        </div>
      )}

      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-fg-muted">
        <span>
          목표 <span className="tabular text-rise">{px(position.targetPrice)}</span>
        </span>
        {position.nextRungPrice !== null && (
          <span>
            다음 매수 <span className="tabular">{px(position.nextRungPrice)}</span>
          </span>
        )}
        <span>
          손절 <span className="tabular text-fall">{px(position.stopPrice)}</span>
        </span>
      </div>

    </li>
  );
}

/**
 * 매수 상태별 배지. (2026-09-19 I-62)
 * 🔴 색만으로 가르지 않는다 - 글자가 뜻을 말한다. "안 삼" 둘은 회색(neutral)으로 가라앉힌다
 */
const BUY_STATE_BADGE: Partial<Record<TargetBuyState, { tone: 'warning' | 'neutral' | 'info'; text: string }>> = {
  buy: { tone: 'warning', text: '매수 구간' },
  held: { tone: 'info', text: '보유 중' },
  belowStop: { tone: 'neutral', text: '손절선 아래 · 안 삼' },
  outOfRank: { tone: 'neutral', text: '순위 밖 · 안 삼' },
};

/**
 * 매수 차수 배지. (2026-10-05 사용자 요청 - "빨강 · 파랑 · 검정과 다르게 구분")
 *
 * 1차 = 청록 · 2차 = 보라 (사용자 선택 - 제안 셋 중 첫째)
 *   🔴 빨강 · 파랑은 이 앱에서 가격 방향(상승 · 하락)이고 검정은 값 글자라 쓰지 않는다
 *   청록 · 보라는 색약(적록)에서도 빨강과 섞이지 않는다. 주황은 경고색(배지 warning)과 겹쳐 뺐다
 *   글자 색만이 아니라 옅은 바탕을 깔아 작은 글씨에서도 눈에 띄게 한다. 다크 모드는 .dark 변형으로 옅은 글자 + 짙은 바탕
 * 3차 이상은 운영에 없다(최대 2단 · BACKLOG 49행 "3단이 찍히면 장치 버그") - 혹시 나오면 중립 배지로 보여 눈에 걸리게 둔다
 */
const RUNG_BADGE: Record<number, string> = {
  1: 'bg-teal-50 text-teal-700 dark:bg-teal-950 dark:text-teal-300',
  2: 'bg-violet-50 text-violet-700 dark:bg-violet-950 dark:text-violet-300',
};

function RungBadge({ rungNo }: { rungNo: number }) {
  return (
    <span
      className={cx(
        'rounded px-1 py-px text-[11px] font-semibold',
        RUNG_BADGE[rungNo] ?? 'bg-badge-neutral text-badge-neutral-fg',
      )}
    >
      {rungNo}차
    </span>
  );
}

function TargetRow({
  target,
  livePrice,
  buyState,
  onOpen,
}: {
  target: BandTarget;
  /** 15초 폴링 시세. 없으면 상태 응답(2분 주기)의 값을 쓴다 */
  livePrice: number | null;
  /** 요약 줄과 같은 함수(targetBuyState)로 판정한 값 - 둘이 어긋나지 않게 밖에서 받는다 */
  buyState: TargetBuyState;
  onOpen: () => void;
}) {
  const current = livePrice ?? target.currentPrice;
  const { px } = useBandPriceFormat();
  // 기준가까지 남은 거리(%)도 살아 있는 현재가로 다시 잰다.
  // 서버 값(2분 주기)을 쓰면 현재가만 새것이고 거리만 헌것인 줄이 생긴다
  const distance =
    current === null || target.bandPrice === 0
      ? target.distancePercent
      : ((current - target.bandPrice) / target.bandPrice) * 100;
  const inBand = distance !== null && distance <= 0;

  return (
    <tr
      onClick={onOpen}
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          onOpen();
        }
      }}
      className={cx(
        'cursor-pointer transition-colors hover:bg-surface-hover',
        // 배경은 "지금 살 수 있는" 줄에만. 안 사는 줄은 흐리게 가라앉힌다 (2026-09-19 I-62)
        buyState === 'buy' && 'bg-surface-muted',
        (buyState === 'belowStop' || buyState === 'outOfRank') && 'opacity-60',
      )}
    >
      {/*
        🔴 표식은 종목명 아래 줄로 내린다 (2026-08-31 사용자 피드백)
          배지를 종목명과 같은 줄에 두면 좁은 화면에서 종목명이 밀려
          두 줄로 접힌다. 표식이 있는 행만 두 줄이 되게 한다 -
          없는 행은 그대로 한 줄이라 표가 들쭉날쭉해지지 않는다.
      */}
      {/*
        🔴 폰에서 종목 칸 폭을 묶는다 (2026-09-16 사용자 피드백 - 여전히 가로 스크롤)
          열을 셋으로 줄였어도 긴 종목명(HD현대에너지솔루션 등)이 한 줄을 고집해 표가 밀렸다.
          한글 이름은 띄어쓰기가 없어 보통 줄바꿈으로는 안 접히므로 overflow-wrap:anywhere 로 접는다.
          사용자: "이름이 긴 건 다음 줄로 나와도 상관없다". sm 이상은 폭이 넉넉해 풀어 둔다.
      */}
      <Td>
        <div className="flex max-w-[7rem] items-baseline gap-2 sm:max-w-none">
          <span className="text-xs text-fg-subtle tabular">{target.swingRank}</span>
          <span className="min-w-0 font-medium [overflow-wrap:anywhere]">{target.stockName}</span>
        </div>
        {/*
          순위 밖은 기준가와 상관없이 늘 붙으면 70줄이 두 줄이 된다 - 기준가 아래로 온 줄에만 붙인다.
          보유 중도 같다. 손절선 아래는 정의상 기준가 아래다
        */}
        {BUY_STATE_BADGE[buyState] && (inBand || buyState === 'belowStop') && (
          <div className="mt-1">
            <Badge tone={BUY_STATE_BADGE[buyState].tone}>
              <span className="text-[10px]">{BUY_STATE_BADGE[buyState].text}</span>
            </Badge>
          </div>
        )}
      </Td>
      {/* 🔴 금액에는 반드시 tabular - 없으면 "13,610원" 이 두 줄로 쪼개진다 */}
      <Td align="right">
        <span className="tabular">{current === null ? '-' : px(current)}</span>
      </Td>
      {/*
        🔴 매수 기준가와 기준가까지를 한 칸에 두 줄로 쌓는다 (2026-09-12)
          열이 넷이면 폰에서 표가 가로로 밀린다. 두 값이 원래 한 쌍이라
          (이 선까지 얼마 남았나) 붙여 두는 편이 읽기도 낫다.
          주도주·섹터의 대비/등락률과 같은 방식이다.
      */}
      <Td align="right">
        <span className="block tabular">{px(target.bandPrice)}</span>
        <span
          className={cx(
            'block text-xs tabular',
            inBand ? 'text-rise' : 'text-fg-subtle',
          )}
        >
          {distance === null ? '-' : formatPercent(distance)}
        </span>
      </Td>
    </tr>
  );
}

/**
 * 보유 종목 칸 읽는 법. (2026-09-16 사용자 요청 - "보유 N종목" 옆 돋보기)
 *
 * 🔴 숫자는 서버 설정(config)에서 받는다 - 손으로 적으면 설정을 바꿨을 때 도움말이 거짓말을 한다.
 *   config 가 아직 없으면(첫 로딩) 배수·% 자리를 "설정값" 으로 둔다.
 */
function holdingsHelp(config: BandConfig | null): IndicatorHelp {
  const bounce = config ? `${config.bouncePct}%` : '설정값';
  const stopAtr = config ? `${config.stopAtrMultiple}` : '설정값';
  const rungAtr = config ? `${config.rungAtrMultiple}` : '설정값';
  const maxRungs = config ? `${config.maxRungs}` : '설정값';
  // 챌린저는 나눠 사지 않는다 - "2·3차" 설명이 거짓말이 된다 (2026-09-19 I-59)
  const oneShot = config !== null && config.maxRungs <= 1;
  const cooldown = config ? `${config.stopCooldownDays}일` : '설정된 기간';
  return {
    label: '보유 종목 칸 읽는 법',
    hint: '',
    detail: [
      oneShot
        ? '[N단] — 이 계좌는 나눠 사지 않아 늘 1단입니다. 첫 매수에 종목당 한도를 한 번에 씁니다.'
        : `[N단] — 나눠 산 횟수입니다. 가격이 더 내려오면 최대 ${maxRungs}단까지 더 삽니다.`,
      '평단 × 주수 = 합계 — 산 가격의 평균(평단), 보유 수량, 그 곱인 투입 금액입니다. 옆의 %는 평단 대비 지금 가격(아직 팔지 않은 손익률)입니다. 빨강은 이익, 파랑은 손실입니다.',
      '전날 대비 — 오늘 가격이 어제 종가보다 얼마나 움직였는지(한 주당 금액과 %)입니다. 보유분은 그 움직임 × 보유 수량으로, 이 종목이 오늘 계좌를 얼마나 움직였는지입니다. 장이 닫혀 있으면 회색입니다.',
      '매수 — 산 시각입니다. 2차 이상을 샀으면 차수마다 한 줄씩(시각 · 가격 × 수량) 최근 매수가 위에 나옵니다. "N일째" 는 1차 매수일부터 센 보유 기간입니다.',
      '현재 · 평가액 — 지금 가격과 그 가격으로 계산한 보유 금액입니다. 현재가는 평단보다 높으면 빨강, 낮으면 파랑입니다.',
      oneShot
        ? `목표 — 평단 +${bounce} 가격입니다. 지금 가격이 여기에 닿으면 전부 팝니다.`
        : `목표 — 평단 +${bounce} 가격입니다. 지금 가격이 여기에 닿으면 전부 팝니다. 2·3차를 사서 평단이 내려가면 목표도 같이 내려갑니다.`,
      oneShot
        ? '다음 매수 — 이 계좌는 나눠 사지 않아 나오지 않습니다.'
        : `다음 매수 — 2·3차를 사는 가격(직전 매수가 − ${rungAtr} ATR)입니다. ${maxRungs}단을 다 샀으면 나오지 않습니다. 계좌에 예수금이 모자라면 이 가격에 닿아도 사지 못하고 넘어갑니다.`,
      `손절 — 1차 매수 때 정해 두는 가격선(매수 기준가 − ${stopAtr} ATR)입니다. 여기까지 내려오면 전부 팔고 ${cooldown} 동안 그 종목을 다시 사지 않습니다. ATR 은 그 종목이 하루에 보통 움직이는 폭이라, 평단 대비 몇 %인지는 종목마다 다릅니다.`,
    ],
  };
}

/**
 * 감시 대상 표의 각 열 설명. (2026-08-28 사용자 요청)
 *
 * 입문자가 표를 처음 봤을 때 "이 숫자가 뭐지" 에 답한다.
 * 열 이름이 바뀌면 여기도 같이 바뀌어야 한다 - 안 그러면 도움말이 거짓말을 한다.
 */
function ColumnHelpModal({
  open,
  onClose,
  buyPercentile,
  bandWindow,
}: {
  open: boolean;
  onClose: () => void;
  buyPercentile: number;
  /** 밴드 계산 기간(거래일). 🔴 하드코딩하지 않는다 - 설정이 바뀌면 도움말이 거짓말이 된다 */
  bandWindow: number;
}) {
  if (!open) {
    return null;
  }

  // 한 달을 21거래일로 본다. 120거래일 → 약 6개월
  const months = Math.round(bandWindow / 21);

  const items: { term: string; body: string }[] = [
    {
      term: '종목 (앞의 숫자)',
      body:
        '왕복성 순위입니다. 왕복성이란 고점과 저점 사이를 얼마나 자주, 크게 ' +
        '오갔는지를 재는 값으로, 이 값이 높은 종목은 한 방향으로 쭉 가기보다 ' +
        '오르내림을 반복합니다. 지난 1년 기준으로 1위부터 줄 세운 것이며, ' +
        '여러 종목이 동시에 매수 구간에 오면 이 순서로 삽니다.',
    },
    {
      term: '매수 순서(예상)',
      body:
        '표 위의 [매수 순서(예상)]을 누르면 실제로 사게 될 순서로 줄 세웁니다. 감시는 1분마다 하고 ' +
        '먼저 매수 기준가에 닿은 종목부터 사므로, 지금 매수 구간인 종목을 순위 순으로 맨 앞에, ' +
        '그다음은 기준가에 가까운 종목부터 둡니다. 예수금이나 자리가 모자라면 뒤는 못 삽니다.',
    },
    {
      term: '"안 삼" 표시',
      body:
        '기준가 아래로 왔어도 사지 않는 종목입니다. 손절선 아래 — 이미 손절 가격까지 떨어져 ' +
        '있어 사자마자 팔게 되므로 사지 않습니다. 순위 밖 — 저점매수(챌린저)는 ' +
        '감시 종목 중 왕복성 상위 일부만 새로 삽니다(계좌 설정의 "새로 사는 종목"). ' +
        '보유 중 — 이미 들고 있어 새로 사지 않습니다.',
    },
    {
      term: '현재가',
      body:
        '지금 시장 가격입니다. 15초마다 갱신되며, 장이 닫혀 있으면 마지막 종가가 보입니다.',
    },
    {
      term: '매수 기준가',
      body:
        `이 아래로 내려오면 사는 선입니다. 종목마다 최근 ${months}개월(${bandWindow}거래일) ` +
        `종가를 싼 순서로 줄 세워 아래에서 ${buyPercentile}% 되는 지점으로 잡습니다 ` +
        `(이것을 "하위 ${buyPercentile}% 분위"라고 합니다). "최근 흐름에서 싼 편인 가격" ` +
        `이라는 뜻이며, 매일 새 일봉으로 다시 계산됩니다. ` +
        `이 퍼센트가 클수록 더 자주 사게 되고, 작을수록 더 깊이 기다렸다 삽니다.`,
    },
    {
      term: '기준가까지 (매수 기준가 아랫줄)',
      body:
        '현재가가 매수 기준가보다 몇 % 위에 있는지입니다. 0%에 가까울수록 매수가 ' +
        '임박한 것이고, 음수(-)면 이미 기준가 아래라 매수 구간입니다(그 행은 배경이 ' +
        '달라집니다). 매수 기준가 바로 아래 줄에 작게 붙어 있으며, 열 제목의 ' +
        '[기준가까지]를 누르면 매수에 가까운 종목부터 볼 수 있습니다.',
    },
  ];

  return (
    <Modal open onClose={onClose} maxWidth="sm:max-w-md">
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-lg font-semibold text-fg">감시 대상 표 읽는 법</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            className="rounded p-1 text-fg-subtle hover:bg-surface-hover hover:text-fg-muted"
          >
            ✕
          </button>
        </div>

        <dl className="mt-4 space-y-3">
          {items.map((item) => (
            <div key={item.term}>
              <dt className="text-sm font-semibold text-fg">{item.term}</dt>
              <dd className="mt-0.5 text-sm leading-relaxed text-fg-muted">{item.body}</dd>
            </div>
          ))}
        </dl>

        <p className="mt-4 border-t border-line pt-3 text-xs text-fg-subtle">
          열 제목을 누르면 그 값으로 정렬됩니다. [매수 기준가 / 기준가까지]는 두 이름이
          각각 눌리며, 한 번 더 누르면 반대 방향, 세 번째에는 순위순으로 돌아옵니다.
        </p>
      </div>
    </Modal>
  );
}

/**
 * 청산 이력. (2026-09-12 페이지네이션 추가 · 보유 다음으로 이동)
 *
 * 🔴 왜 여기만 페이지인가 — 다섯 섹션 중 유일하게 끝없이 늘어난다
 *   보유는 종목당 예산(20% · 챌린저 10%)이 상한을 만들고 감시 대상은 설정값으로 고정이지만,
 *   청산은 회차를 거듭할수록 쌓이기만 한다(2회차 11일에 45건). 그리고 궁금한 것은
 *   늘 최근 몇 건이다. 추천 3계좌가 이미 같은 방식(10건씩)이라 두 계열의 조작이 같아진다.
 *
 * 🔴 2026-09-16 부터 서버가 이번 회차 청산을 전부 준다 (사용자 요청 "30건 말고 전부")
 *   전에는 최근 30건만 받아 그 안에서 넘겼다. 회차당 청산이 수십~백여 건이라 전부 받아도
 *   가볍고, 페이지는 화면 길이만 다룬다. 문구는 받은 건수가 전체보다 적을 때만 "최근 N건" 을 말한다 —
 *   배포 순서가 어긋나 옛 서버가 30건만 줘도 거짓말을 하지 않는다.
 */
function ClosedCard({
  data,
  onOpen,
}: {
  data: BandStatus;
  onOpen: (stockId: number) => void;
}) {
  const [page, setPage] = useState(0);
  const { px, signedPx, usd } = useBandPriceFormat();

  if (data.closed.length === 0) {
    return null;
  }

  const totalPages = Math.ceil(data.closed.length / CLOSED_PAGE_SIZE);
  // 목록이 줄면(회차 초기화 등) 보던 페이지가 범위를 넘는다
  const safePage = Math.min(page, totalPages - 1);
  const visible = data.closed.slice(
    safePage * CLOSED_PAGE_SIZE,
    (safePage + 1) * CLOSED_PAGE_SIZE,
  );

  const sum = (pick: (row: BandClosed) => number) =>
    data.closed.reduce((acc, row) => acc + pick(row), 0);
  // 받은 것이 전체보다 적을 때만 "최근" 이다
  const partial = data.closed.length < data.totalClosed;

  return (
    <Card
      title={`청산 ${data.totalClosed}건${partial ? ` (최근 ${data.closed.length}건)` : ''}`}
    >
      <Table>
        <thead>
          <tr>
            <Th>종목</Th>
            <Th align="right">평단 → 매도</Th>
            {/* ? 는 실현 손익이 무엇인지 설명한다 (profitHelp.ts). Th 가 nowrap 이라 버튼이 아랫줄로 안 떨어진다 */}
            <Th align="right">
              <span className="inline-flex items-center">
                순손익
                <HelpButton help={PROFIT_HELP} />
              </span>
            </Th>
            <Th align="right">이유</Th>
          </tr>
        </thead>
        <tbody>
          {visible.map((row) => (
            <ClosedRow key={row.id} row={row} onOpen={() => onOpen(row.stockId)} />
          ))}

          {/*
            합계 행. (2026-09-10 사용자 요청)

            🔴 이 페이지가 아니라 받아온 최근 N건 전체의 합이다
              페이지마다 합계를 따로 내면 "이 계좌가 얼마를 벌었나" 를 볼 수 없다.
              다만 응답이 최근 30건만 담으므로 전체 합도 아니다 - 아래 각주가 그것을 말한다.
          */}
          <tr className="border-t-2 border-line font-semibold">
            <Td>
              {partial ? '최근' : '전체'} {data.closed.length}건 합계
            </Td>
            <Td align="right">
              <span className="tabular text-xs font-normal text-fg-subtle">
                {px(sum((r) => r.avgPrice * r.quantity))}
                {' → '}
                {px(sum((r) => r.sellPrice * r.quantity))}
              </span>
            </Td>
            <Td align="right">
              <span className={cx('tabular', changeColorClass(sum((r) => r.realizedPnl)))}>
                {/* 국내는 원 단위 숫자만(전과 같다) · 🇺🇸 미국은 달러 손익 아래 원화 손익 */}
                {usd ? signedPx(sum((r) => r.realizedPnl)) : formatSignedNumber(sum((r) => r.realizedPnl))}
              </span>
              {usd && (
                <span className={cx('block text-xs tabular', changeColorClass(sum((r) => r.realizedPnlKrw ?? 0)))}>
                  원화 {bandPriceFormat('KRW').signedPx(sum((r) => r.realizedPnlKrw ?? 0))}
                </span>
              )}
            </Td>
            <Td align="right">{''}</Td>
          </tr>
        </tbody>
      </Table>
      <Pager page={safePage} totalPages={totalPages} onChange={setPage} />
      <p className="mt-2 text-xs text-fg-subtle">
        단가 아래 작은 숫자는 수량을 곱한 금액입니다. 합계는 이 페이지가 아니라{' '}
        {partial
          ? `받아온 최근 ${data.closed.length}건 전체를 더한 값이며, 전체 ${data.totalClosed}건의 성적은 위 계좌 카드의 "이번 회차" 수익률이 말합니다.`
          : `이번 회차 청산 ${data.closed.length}건 전체를 더한 값입니다.`}
      </p>
    </Card>
  );
}

/** 회고 한 페이지 분량. 한 건이 3~5문장이라 5건이면 화면이 충분히 길다. */
const REVIEW_PAGE_SIZE = 5;

/**
 * 청산 회고. (docs/impl/87)
 *
 * 🔴 회고의 단위가 포지션이다 - "3번 나눠 사서 +3% 에 팔았다"(익절폭은 계좌마다 +3/+5/+4%) 가 하나의 이야기다.
 *   기존 3계좌는 체결 하나가 회고 하나라 건수를 비교하면 어긋난다.
 */
function BandReviews({ reviews }: { reviews: BandReview[] }) {
  const [page, setPage] = useState(0);

  const totalPages = Math.ceil(reviews.length / REVIEW_PAGE_SIZE);
  // 목록이 줄면(초기화 등) 보고 있던 페이지가 범위를 넘는다
  const safePage = Math.min(page, totalPages - 1);
  const visible = reviews.slice(
    safePage * REVIEW_PAGE_SIZE,
    (safePage + 1) * REVIEW_PAGE_SIZE,
  );

  return (
    <Card title={`회고 ${reviews.length}건`}>
      <ul className="space-y-3">
        {visible.map((review) => (
          <li key={review.positionId} className="rounded-lg border border-line p-3">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="text-sm font-medium">{review.stockName}</span>
              <Badge tone={sellReasonTone(review.sellReason)}>
                {sellReasonLabel(review.sellReason)}
              </Badge>
              {review.returnPercent !== null && (
                <span className={cx('text-xs tabular', changeColorClass(review.returnPercent))}>
                  {formatPercent(review.returnPercent)}
                </span>
              )}
              <span className="text-xs text-fg-subtle">{formatDateTime(review.closedAt)}</span>
            </div>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-fg-muted">
              {toThousands(review.content)}
            </p>
          </li>
        ))}
      </ul>

      <Pager page={safePage} totalPages={totalPages} onChange={setPage} />

      <p className="mt-3 text-xs text-fg-subtle">
        {REVIEW_PAUSED_NOTE} 위 목록은 그 전에 쓰인 회고로, AI 가 지난 거래를 돌아본 글이며 앞으로의 주가를 예측하지 않습니다.
      </p>
    </Card>
  );
}

function ClosedRow({ row, onOpen }: { row: BandClosed; onOpen: () => void }) {
  const { px, signedPx, usd } = useBandPriceFormat();
  return (
    <tr
      onClick={onOpen}
      className="cursor-pointer transition-colors hover:bg-surface-hover"
    >
      <Td>
        <div className="flex items-baseline gap-1">
          <span className="font-medium">{row.stockName}</span>
          <span className="text-xs text-fg-subtle tabular">
            {row.rungs}단 · {row.quantity}주
          </span>
        </div>
      </Td>
      {/*
        🔴 매수 시각도 보여준다 (2026-08-31 사용자 요청)

          매도 시각만 있어서 "언제 사서 언제 팔았나" 를 알 수 없었다.
          이 계좌는 평균 이틀 만에 회전하고 같은 날 사고파는 일도 있어
          날짜만으로는 부족하다 - 시·분까지 적는다.

          매수는 **1차 매수** 시각이다. 사다리로 2·3차를 더 샀다면
          그 시각들은 보유 카드의 체결 원장에 있다.
      */}
      <Td align="right">
        <span className="tabular text-sm">
          {px(row.avgPrice)} → {px(row.sellPrice)}
        </span>
        {/* 단가 아래 둘째 줄에 수량을 곱한 금액 (2026-09-10 사용자 요청) */}
        <span className="block text-xs text-fg-subtle tabular">
          {px(row.avgPrice * row.quantity)} → {px(row.sellPrice * row.quantity)}
        </span>
        <span className="block text-xs text-fg-subtle tabular">
          매수 {formatDateTime(row.firstBuyAt)}
        </span>
        <span className="block text-xs text-fg-subtle tabular">
          매도 {formatDateTime(row.closedAt)}
        </span>
      </Td>
      <Td align="right">
        <span className={cx('tabular', changeColorClass(row.realizedPnl))}>
          {usd ? signedPx(row.realizedPnl) : formatSignedNumber(row.realizedPnl)}
        </span>
        {/* 🇺🇸 달러로 벌고 원화로 잃을 수 있다 — 원화 손익을 함께 (2026-09-28) */}
        {usd && row.realizedPnlKrw !== null && (
          <span className={cx('block text-xs tabular', changeColorClass(row.realizedPnlKrw))}>
            원화 {bandPriceFormat('KRW').signedPx(row.realizedPnlKrw)}
          </span>
        )}
        {row.returnPercent !== null && (
          <span className={cx('block text-xs', changeColorClass(row.returnPercent))}>
            {formatPercent(row.returnPercent)}
          </span>
        )}
      </Td>
      <Td align="right">
        <Badge tone={sellReasonTone(row.sellReason)}>
          {sellReasonLabel(row.sellReason)}
        </Badge>
      </Td>
    </tr>
  );
}


/**
 * 청산 사유 배지. (2026-09-19 I-59)
 *
 * 🔴 회차 종료를 따로 가른다
 *   전에는 TARGET 이 아니면 전부 빨간 "구조 이탈" 로 그렸다. 2회차 종료(9/18) 일괄 청산 15건이
 *   손절처럼 보였다 - 매매 판단이 아니라 기간 때문에 판 것인데 규칙이 실패한 것으로 읽힌다.
 */
function sellReasonLabel(reason: BandPositionSellReason): string {
  return reason === 'TARGET' ? '반등 익절' : reason === 'ROUND_END' ? '회차 종료' : '구조 이탈';
}

function sellReasonTone(reason: BandPositionSellReason): 'success' | 'danger' | 'neutral' {
  return reason === 'TARGET' ? 'success' : reason === 'ROUND_END' ? 'neutral' : 'danger';
}

type BandPositionSellReason = 'TARGET' | 'STOP' | 'ROUND_END';
